import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { proxyImg } from "@/utils/proxyImg";

export const ProjectDetail = ({ project, open, onClose }) => {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    setLightboxIndex(null);
  }, [project]);

  // Lock body scroll when modal or lightbox is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const prevImage = useCallback(() => {
    if (!project) return;
    setLightboxIndex((p) => (p > 0 ? p - 1 : project.images.length - 1));
  }, [project]);
  const nextImage = useCallback(() => {
    if (!project) return;
    setLightboxIndex((p) => (p < project.images.length - 1 ? p + 1 : 0));
  }, [project]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const h = (e) => {
      if (e.key === "ArrowLeft") prevImage();
      else if (e.key === "ArrowRight") nextImage();
      else if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [lightboxIndex, prevImage, nextImage, closeLightbox]);

  // Close modal on Escape (when lightbox is NOT open)
  useEffect(() => {
    if (!open || lightboxIndex !== null) return;
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, lightboxIndex, onClose]);

  if (!project || !open) return null;

  return createPortal(
    <>
      {/* Project Detail Modal */}
      <div style={{ position: "fixed", inset: 0, zIndex: 50 }} data-testid="project-detail-modal">
        {/* Backdrop */}
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.8)" }} onClick={onClose} />

        {/* Content */}
        <div style={{
          position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)",
          width: "min(960px, 95vw)", maxHeight: "90vh", overflowY: "auto",
          background: "#F5F5F3", zIndex: 1
        }}>
          {/* Close button */}
          <button onClick={onClose} style={{
            position: "absolute", top: 12, right: 12, zIndex: 5,
            width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center",
            borderRadius: "4px", background: "rgba(0,0,0,0.5)", border: "none", cursor: "pointer", color: "white"
          }}>
            <X size={16} />
          </button>

          {/* Hero Image */}
          <div style={{ cursor: "pointer" }} onClick={() => setLightboxIndex(0)} data-testid="project-hero-image">
            <img src={proxyImg(project.images[0], 1200)} alt={project.title}
              style={{ width: "100%", aspectRatio: "16/9", objectFit: "cover", display: "block" }} />
          </div>

          {/* Project Info */}
          <div style={{ padding: "32px 32px" }} data-testid="project-info">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
              <span className="label-caps" style={{ padding: "4px 12px", border: "1px solid #E0E0DE" }}>{project.category}</span>
              <span className="label-caps">{project.year}</span>
              <span className="label-caps">{project.role}</span>
            </div>
            <h2 className="font-['Syne']" style={{ fontWeight: 600, fontSize: "clamp(24px, 3vw, 36px)", marginBottom: 20 }}>{project.title}</h2>
            <p style={{ color: "#737373", lineHeight: 1.7, maxWidth: 600 }}>{project.description}</p>
            <div style={{ marginTop: 28, paddingTop: 28, borderTop: "1px solid #E0E0DE" }}>
              <span className="label-caps" style={{ display: "block", marginBottom: 6 }}>Location</span>
              <span style={{ fontSize: 18 }}>{project.location}</span>
            </div>
          </div>

          {/* Gallery Grid */}
          {project.images.length > 1 && (
            <div style={{ padding: "0 32px 32px" }} data-testid="project-image-grid">
              <span className="label-caps" style={{ display: "block", marginBottom: 16 }}>Gallery</span>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 4 }}>
                {project.images.slice(1).map((img, i) => (
                  <div key={i} style={{ cursor: "pointer", overflow: "hidden" }} onClick={() => setLightboxIndex(i + 1)} data-testid={`project-grid-image-${i}`}>
                    <img src={proxyImg(img, 600)} alt={`${project.title} ${i + 2}`}
                      style={{ width: "100%", aspectRatio: "4/3", objectFit: "cover", display: "block", transition: "transform 0.4s" }}
                      loading="lazy"
                      onMouseOver={(e) => e.target.style.transform = "scale(1.05)"}
                      onMouseOut={(e) => e.target.style.transform = "scale(1)"}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div data-testid="image-lightbox" style={{ position: "fixed", inset: 0, zIndex: 9999 }}>
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.95)" }} onClick={closeLightbox} />

          <button onClick={closeLightbox} data-testid="lightbox-close" style={{
            position: "absolute", top: 16, right: 16, zIndex: 3,
            width: 48, height: 48, display: "flex", alignItems: "center", justifyContent: "center",
            borderRadius: "50%", background: "rgba(255,255,255,0.15)", border: "none", cursor: "pointer", color: "white"
          }}><X size={24} /></button>

          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none", zIndex: 1 }}>
            <img src={proxyImg(project.images[lightboxIndex], 1600)} alt={`${project.title} ${lightboxIndex + 1}`}
              style={{ maxWidth: "75vw", maxHeight: "85vh", objectFit: "contain" }} />
          </div>

          {project.images.length > 1 && (
            <button onClick={prevImage} data-testid="lightbox-prev" style={{
              position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", zIndex: 3,
              width: 64, height: 64, display: "flex", alignItems: "center", justifyContent: "center",
              borderRadius: "50%", background: "rgba(255,255,255,0.15)", border: "none", cursor: "pointer", color: "white"
            }}><ChevronLeft size={32} /></button>
          )}

          {project.images.length > 1 && (
            <button onClick={nextImage} data-testid="lightbox-next" style={{
              position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", zIndex: 3,
              width: 64, height: 64, display: "flex", alignItems: "center", justifyContent: "center",
              borderRadius: "50%", background: "rgba(255,255,255,0.15)", border: "none", cursor: "pointer", color: "white"
            }}><ChevronRight size={32} /></button>
          )}

          <div style={{ position: "absolute", bottom: 20, left: 0, right: 0, textAlign: "center", color: "rgba(255,255,255,0.6)", fontSize: 14, letterSpacing: 4, zIndex: 3 }}>
            {lightboxIndex + 1} / {project.images.length}
          </div>
        </div>
      )}
    </>,
    document.body
  );
};
